import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Phone, PhoneCall, PhoneOff, PhoneMissed, Mic, MicOff, Radio,
  Clock, CheckCircle, AlertTriangle, Loader2, Sparkles, ExternalLink,
  RotateCcw, Play, Square, FileText, Volume2, Settings, ChevronDown,
  ChevronUp, Copy, Check, History, Zap, Shield,
} from 'lucide-react';
import apiClient from '@/services/api/apiClient';
import SearchableSelect from '@/components/common/SearchableSelect';
import CallProgressStepper from '@/components/common/CallProgressStepper';
import aiCallingService, { ProviderStatus, CallRecord, LiveCallStatus } from './aiCallingService';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';

// ── UI atoms ──────────────────────────────────────────────────────────────────
function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-xl border border-border bg-card ${className}`}>{children}</div>;
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-muted transition-colors">
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// ── Status badge ───────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string; icon: any }> = {
  initiated:    { label: 'Initiating…', color: 'text-blue-600', dot: 'bg-blue-500 animate-pulse', icon: Phone },
  queued:       { label: 'Queued',      color: 'text-amber-600', dot: 'bg-amber-400 animate-pulse', icon: Clock },
  ringing:      { label: 'Ringing',    color: 'text-amber-600', dot: 'bg-amber-500 animate-pulse', icon: PhoneCall },
  registered:   { label: 'Connecting', color: 'text-amber-600', dot: 'bg-amber-500 animate-pulse', icon: Radio },
  'in-progress':{ label: 'Live',       color: 'text-emerald-600', dot: 'bg-emerald-500 animate-pulse', icon: Mic },
  ongoing:      { label: 'Live',       color: 'text-emerald-600', dot: 'bg-emerald-500 animate-pulse', icon: Mic },
  ended:        { label: 'Ended',      color: 'text-muted-foreground', dot: 'bg-gray-400', icon: PhoneOff },
  failed:       { label: 'Failed',     color: 'text-red-600', dot: 'bg-red-500', icon: PhoneMissed },
  error:        { label: 'Error',      color: 'text-red-600', dot: 'bg-red-500', icon: PhoneMissed },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.initiated;
  const Icon = cfg.icon;
  return (
    <div className={`flex items-center gap-2 ${cfg.color}`}>
      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${cfg.dot}`} />
      <Icon className="h-4 w-4" />
      <span className="text-sm font-medium">{cfg.label}</span>
    </div>
  );
}

// ── Provider card ─────────────────────────────────────────────────────────────
function ProviderCard({
  id, info, selected, onSelect
}: { id: string; info: ProviderStatus; selected: boolean; onSelect: () => void }) {
  const LOGOS: Record<string, string> = {
    vapi: '🔊',
    retell: '📞',
  };

  return (
    <button
      onClick={onSelect}
      disabled={!info.configured}
      className={`relative w-full text-left rounded-xl border-2 p-4 transition-all ${
        selected && info.configured
          ? 'border-primary bg-primary/5'
          : info.configured
            ? 'border-border hover:border-primary/40 bg-card'
            : 'border-dashed border-border bg-muted/20 opacity-60 cursor-not-allowed'
      }`}
    >
      {selected && info.configured && (
        <CheckCircle className="absolute top-3 right-3 h-4 w-4 text-primary" />
      )}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-2xl">{LOGOS[id] || '📱'}</span>
        <div>
          <p className="text-sm font-bold">{info.label}</p>
          <p className="text-xs text-muted-foreground">
            {info.configured ? (
              <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Connected
              </span>
            ) : (
              <span className="text-amber-600 dark:text-amber-400">Not configured</span>
            )}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {info.features.map(f => (
          <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            {f}
          </span>
        ))}
      </div>
      {!info.configured && (
        <a
          href="/settings"
          onClick={e => e.stopPropagation()}
          className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Settings className="h-3 w-3" /> Configure in Settings
        </a>
      )}
    </button>
  );
}

// ── Live call monitor ─────────────────────────────────────────────────────────
function LiveCallMonitor({
  callId, provider, onEnded
}: { callId: string; provider: string; onEnded: (status: LiveCallStatus) => void }) {
  const { addToast } = useToast();
  const [status, setStatus] = useState<LiveCallStatus | null>(null);
  const [polling, setPolling] = useState(true);
  const intervalRef = useRef<any>(null);

  const poll = useCallback(async () => {
    try {
      const s = await aiCallingService.getStatus(callId, provider);
      setStatus(s);
      if (['ended', 'error', 'failed'].includes(s.status)) {
        setPolling(false);
        clearInterval(intervalRef.current);
        onEnded(s);
      }
    } catch { /* non-fatal */ }
  }, [callId, provider, onEnded]);

  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, 4000); // poll every 4s
    return () => clearInterval(intervalRef.current);
  }, [poll]);

  const handleEnd = async () => {
    try {
      await aiCallingService.endCall(callId, provider);
      setPolling(false);
      clearInterval(intervalRef.current);
      addToast({ type: 'success', title: 'Call ended' });
    } catch (e: any) {
      addToast({ type: 'error', title: 'End call failed', description: e.message });
    }
  };

  const currentStatus = status?.status || 'initiated';
  const isLive = ['in-progress', 'ongoing', 'ringing', 'registered'].includes(currentStatus);

  return (
    <Card className="p-5 space-y-4">
      {/* Status header */}
      <div className="flex items-center justify-between">
        <StatusBadge status={currentStatus} />
        {isLive && (
          <button onClick={handleEnd}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors">
            <PhoneOff className="h-3.5 w-3.5" /> End Call
          </button>
        )}
        {!polling && (
          <span className="text-xs text-muted-foreground">Call ended</span>
        )}
      </div>

      {/* Progress stepper — what's happening right now */}
      <CallProgressStepper status={currentStatus} />

      {/* Duration */}
      {status?.duration != null && (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Duration:</span>
          <span className="font-medium">{Math.floor(status.duration / 60)}m {status.duration % 60}s</span>
        </div>
      )}

      {/* Live transcript */}
      {status?.transcript && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Mic className="h-3.5 w-3.5" /> Live Transcript
            </p>
            <CopyBtn text={status.transcript} />
          </div>
          <div className="bg-muted/30 rounded-lg p-3 max-h-48 overflow-y-auto">
            <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">
              {status.transcript}
            </pre>
          </div>
        </div>
      )}

      {/* Summary after call ends */}
      {status?.summary && (
        <div className="space-y-2 pt-2 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Call Summary
          </p>
          <p className="text-sm leading-relaxed">{status.summary}</p>
        </div>
      )}

      {/* Recording */}
      {status?.recordingUrl && (
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <Volume2 className="h-4 w-4 text-primary" />
          <a href={status.recordingUrl} target="_blank" rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1">
            Listen to recording <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {/* End reason */}
      {status?.endReason && (
        <p className="text-xs text-muted-foreground">End reason: {status.endReason}</p>
      )}

      {polling && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Monitoring call in real-time…
        </div>
      )}
    </Card>
  );
}

// ── Call history ──────────────────────────────────────────────────────────────
function CallHistory({ contactId }: { contactId: string }) {
  const [records, setRecords] = useState<CallRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!contactId) { setLoading(false); return; }
    setLoading(true);
    aiCallingService.getHistory(contactId)
      .then(r => setRecords(r.data))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    if (!contactId) return;
    setSyncing(true);
    try { await aiCallingService.syncContact(contactId); load(); }
    catch { /* ignore */ }
    finally { setSyncing(false); }
  };

  const PROVIDER_BADGE: Record<string, string> = {
    vapi: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
    retell: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">
          {records.length} call{records.length === 1 ? '' : 's'} on record
        </span>
        <button onClick={sync} disabled={syncing}
          className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border hover:border-primary/40 disabled:opacity-50 transition-colors">
          {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
          Sync transcript from provider
        </button>
      </div>

      {records.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No AI calls yet for this contact. If you dialed from the provider directly, click “Sync transcript”.
        </div>
      )}

      {records.map(r => {
        const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.ended;
        const isOpen = expanded === r.id;
        const hasDetail = !!(r.transcript || r.summary);
        return (
          <Card key={r.id} className="p-4">
            <div className="flex items-center gap-3">
              <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${PROVIDER_BADGE[r.provider] || 'bg-muted text-muted-foreground'}`}>
                    {r.provider}
                  </span>
                  <span className="text-xs font-medium">{CALL_TYPE_LABELS[r.callType] || r.callType}</span>
                  <span className={`text-xs ${cfg.color}`}>{cfg.label}</span>
                  {r.duration ? (
                    <span className="text-xs text-muted-foreground">· {Math.floor(r.duration / 60)}m {r.duration % 60}s</span>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {new Date(r.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {r.phoneNumber ? ` · ${r.phoneNumber}` : ''}
                </p>
              </div>
              <div className="flex gap-1.5 shrink-0 items-center">
                {r.recordingUrl && (
                  <a href={r.recordingUrl} target="_blank" rel="noopener noreferrer"
                    className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors" title="Recording">
                    <Volume2 className="h-3.5 w-3.5" />
                  </a>
                )}
                {hasDetail && (
                  <button onClick={() => setExpanded(isOpen ? null : r.id)}
                    className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors" title="Transcript & summary">
                    {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            </div>

            {isOpen ? (
              <div className="mt-3 space-y-3 border-t border-border pt-3">
                {r.summary && (
                  <div>
                    <div className="text-xs font-semibold flex items-center gap-1 mb-1"><Sparkles className="h-3.5 w-3.5 text-primary" /> Summary</div>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{r.summary}</p>
                  </div>
                )}
                {r.transcript && (
                  <div>
                    <div className="text-xs font-semibold flex items-center gap-1 mb-1"><FileText className="h-3.5 w-3.5 text-primary" /> Full transcript</div>
                    <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans max-h-80 overflow-y-auto bg-muted/40 rounded-lg p-3">{r.transcript}</pre>
                  </div>
                )}
              </div>
            ) : (
              r.transcript && (
                <p className="mt-2 text-xs text-muted-foreground line-clamp-2 pl-4">{r.transcript.slice(0, 200)}…</p>
              )
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
const CALL_TYPE_LABELS: Record<string, string> = {
  CALL1: 'Alignment Call #1',
  CALL2: 'Confirmation Call #2',
  CALL3: 'Follow-up Call',
};

export default function AiCallingPage() {
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();

  const [contacts, setContacts] = useState<any[]>([]);
  const [journeys, setJourneys] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState(searchParams.get('contactId') || '');
  const [selectedContactLabel, setSelectedContactLabel] = useState('');
  const [selectedJourney, setSelectedJourney] = useState('');
  const [contactInfo, setContactInfo] = useState<any>(null);

  const [providers, setProviders] = useState<Record<string, ProviderStatus>>({});
  const [selectedProvider, setSelectedProvider] = useState<'vapi' | 'retell'>('vapi');
  const [callType, setCallType] = useState<'CALL1' | 'CALL2' | 'CALL3'>('CALL1');
  const [phoneOverride, setPhoneOverride] = useState('');
  const [agentNote, setAgentNote] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [activeTab, setActiveTab] = useState<'dial' | 'history'>('dial');
  const [initiating, setInitiating] = useState(false);
  const [activeCall, setActiveCall] = useState<{ callId: string; provider: string } | null>(null);
  const [lastEndedCall, setLastEndedCall] = useState<LiveCallStatus | null>(null);

  // Load contacts and providers
  useEffect(() => {
    apiClient.get('/api/contacts?limit=100').then(r => setContacts(r.data?.data || []));
    aiCallingService.getProviders().then(setProviders).catch(() => {});
  }, []);

  // Load journey when contact changes
  useEffect(() => {
    if (!selectedContact) { setJourneys([]); setContactInfo(null); return; }
    Promise.all([
      apiClient.get(`/api/abm-journeys/contact/${selectedContact}`).catch(() => null),
      apiClient.get(`/api/contacts/${selectedContact}`).catch(() => null),
    ]).then(([journeyRes, contactRes]) => {
      if (journeyRes?.data?.id) { setJourneys([journeyRes.data]); setSelectedJourney(journeyRes.data.id); }
      if (contactRes?.data) {
        setContactInfo(contactRes.data);
        const c = contactRes.data;
        if (!selectedContactLabel) setSelectedContactLabel(`${c.firstName || ''} ${c.lastName || ''}`.trim());
      }
    });
  }, [selectedContact]);

  // Auto-select first configured provider
  useEffect(() => {
    if (providers.vapi?.configured) setSelectedProvider('vapi');
    else if (providers.retell?.configured) setSelectedProvider('retell');
  }, [providers]);

  const startCall = async () => {
    if (!selectedContact) return addToast({ type: 'error', title: 'Select a contact first' });
    const phone = phoneOverride || contactInfo?.phone || contactInfo?.mobilePhone || '';
    if (!phone) return addToast({ type: 'error', title: 'No phone number', description: 'Add a phone number to this contact or enter one manually.' });
    if (!providers[selectedProvider]?.configured) {
      return addToast({ type: 'error', title: `${selectedProvider === 'vapi' ? 'Vapi' : 'Retell'} not configured`, description: 'Go to Settings → Integrations to add your API key.' });
    }

    setInitiating(true);
    setLastEndedCall(null);
    try {
      const r = await aiCallingService.initiateCall({
        contactId: selectedContact,
        journeyId: selectedJourney || undefined,
        provider: selectedProvider,
        callType,
        phoneNumber: phoneOverride || undefined,
        agentNote: agentNote || undefined,
      });
      setActiveCall({ callId: r.callId, provider: selectedProvider });
      addToast({ type: 'success', title: 'Call initiated', description: r.message });
    } catch (e: any) {
      addToast({ type: 'error', title: 'Call failed', description: e.response?.data?.error || e.message });
    } finally { setInitiating(false); }
  };

  const handleCallEnded = (status: LiveCallStatus) => {
    setLastEndedCall(status);
    setActiveCall(null);
    addToast({ type: 'success', title: 'Call completed', description: status.endReason || 'Call ended' });
  };

  const hasAnyProvider = Object.values(providers).some(p => p.configured);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="AI Voice Calling"
        description="Launch outbound AI voice calls via Vapi.ai or Retell AI — powered by your ABM journey context"
        icon={Phone}
      />

      {/* No provider warning */}
      {!hasAnyProvider && Object.keys(providers).length > 0 && (
        <Card className="p-4 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">No voice providers configured</p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                Add your Vapi.ai or Retell AI API key in Settings → Integrations to enable AI voice calls.
              </p>
              <a href="/settings" className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline">
                <Settings className="h-3 w-3" /> Go to Settings
              </a>
            </div>
          </div>
        </Card>
      )}

      {/* Provider selection */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Voice Provider</p>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(providers).map(([id, info]) => (
            <ProviderCard key={id} id={id} info={info}
              selected={selectedProvider === id}
              onSelect={() => setSelectedProvider(id as 'vapi' | 'retell')} />
          ))}
          {Object.keys(providers).length === 0 && (
            <div className="col-span-2 flex justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1">
        {[{ id: 'dial', label: 'Make a Call', icon: Phone }, { id: 'history', label: 'Call History', icon: History }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
              activeTab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* Dial tab */}
      {activeTab === 'dial' && (
        <div className="space-y-4">
          {/* Contact / journey selector */}
          <Card className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Contact <span className="text-foreground">*</span></label>
                <SearchableSelect
                  value={selectedContact}
                  valueLabel={selectedContactLabel}
                  placeholder="Search contact…"
                  onChange={(id, opt) => { setSelectedContact(id); setSelectedContactLabel(opt?.label || ''); setSelectedJourney(''); }}
                  fetchOptions={async (q) => {
                    const { data } = await apiClient.get(`/api/contacts`, { params: { limit: 25, ...(q ? { search: q } : {}) } });
                    return (data.data || []).map((c: any) => ({ id: c.id, label: `${c.firstName} ${c.lastName}`.trim(), sublabel: c.account?.name || c.title || c.email }));
                  }}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Journey (for context)</label>
                <select value={selectedJourney} onChange={e => setSelectedJourney(e.target.value)}
                  disabled={!journeys.length}
                  className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background disabled:opacity-50">
                  <option value="">{journeys.length ? '— Select journey —' : '— No journey —'}</option>
                  {journeys.map(j => <option key={j.id} value={j.id}>Stage {j.currentStage}/14 · {j.completedStages?.length || 0} done</option>)}
                </select>
              </div>
            </div>

            {/* Contact phone display */}
            {contactInfo && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{contactInfo.firstName} {contactInfo.lastName}</p>
                  <p className="text-xs text-muted-foreground">
                    {contactInfo.phone || contactInfo.mobilePhone || (
                      <span className="text-amber-600 dark:text-amber-400">No phone number on record</span>
                    )}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Call Type</label>
                <select value={callType} onChange={e => setCallType(e.target.value as any)}
                  className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
                  {Object.entries(CALL_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Phone (override)</label>
                <input value={phoneOverride} onChange={e => setPhoneOverride(e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
              </div>
            </div>

            {/* Advanced */}
            <button onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Advanced options
            </button>
            {showAdvanced && (
              <textarea value={agentNote} onChange={e => setAgentNote(e.target.value)}
                placeholder="Note to AI agent for this call (e.g. 'They mentioned a Q3 budget — probe gently')"
                rows={3}
                className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />
            )}
          </Card>

          {/* What the AI will say */}
          <Card className="p-4 space-y-2 bg-primary/5 border-primary/20">
            <p className="text-xs font-semibold text-primary flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> What the AI agent will do
            </p>
            <ul className="space-y-1 text-xs text-foreground">
              <li className="flex items-start gap-2"><Zap className="h-3 w-3 text-primary shrink-0 mt-0.5" />Open with a context-specific first message referencing their known pain axis</li>
              <li className="flex items-start gap-2"><Zap className="h-3 w-3 text-primary shrink-0 mt-0.5" />Ask qualifying questions aligned to the {callType === 'CALL1' ? 'Alignment Call #1' : callType === 'CALL2' ? 'Confirmation Call #2' : 'Follow-up Call'} objective</li>
              <li className="flex items-start gap-2"><Zap className="h-3 w-3 text-primary shrink-0 mt-0.5" />Reference prior stage intelligence — not a generic cold call</li>
              <li className="flex items-start gap-2"><Shield className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" />Close with a specific next step (technical session, document review, etc.)</li>
            </ul>
          </Card>

          {/* Initiate button */}
          <button onClick={startCall} disabled={initiating || !selectedContact}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
            {initiating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
            {initiating ? 'Initiating call…' : `Launch AI Call via ${providers[selectedProvider]?.label || selectedProvider}`}
          </button>

          {/* Initiating — showcase what's happening before the call is live */}
          {initiating && !activeCall && (
            <Card className="p-5">
              <CallProgressStepper initiating />
            </Card>
          )}

          {/* Active call monitor */}
          {activeCall && (
            <LiveCallMonitor
              callId={activeCall.callId}
              provider={activeCall.provider}
              onEnded={handleCallEnded}
            />
          )}

          {/* Post-call result */}
          {lastEndedCall && !activeCall && (
            <Card className="p-4 space-y-3 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Call completed</p>
                {lastEndedCall.duration && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {Math.floor(lastEndedCall.duration / 60)}m {lastEndedCall.duration % 60}s
                  </span>
                )}
              </div>
              {lastEndedCall.summary && (
                <p className="text-sm">{lastEndedCall.summary}</p>
              )}
              {lastEndedCall.transcript && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">Transcript</p>
                    <CopyBtn text={lastEndedCall.transcript} />
                  </div>
                  <pre className="text-xs whitespace-pre-wrap font-sans bg-muted/30 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {lastEndedCall.transcript}
                  </pre>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <a href={`/voice-agent?contactId=${selectedContact}&tab=postcall`}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                  <FileText className="h-3 w-3" /> Run Post-Call Analysis
                </a>
                {lastEndedCall.recordingUrl && (
                  <a href={lastEndedCall.recordingUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
                    <Volume2 className="h-3 w-3" /> Listen
                  </a>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* History tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {!selectedContact ? (
            <Card className="p-8 text-center">
              <History className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Select a contact to view their AI call history</p>
            </Card>
          ) : (
            <CallHistory contactId={selectedContact} />
          )}
        </div>
      )}
    </div>
  );
}
